import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Eye, EyeSlash, Edit2, Trash, Home, SearchNormal1, Add, ArrowLeft2, ArrowRight2,
  Map1, Link2, Location, GalleryAdd,
} from "iconsax-reactjs";
import { db, storage } from "../firebase";
import {
  collection, onSnapshot, doc, setDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  buildListingDoc, softDeleteListing, setListingHidden, deleteListingPermanently,
} from "../utils/listingPayload";
import { getListingCardInfo, getListingTypeOf, getListingStatus } from "../utils/listingDisplay";
import { suggestTitle, toNumber } from "../shared/listingRules";
import {
  LISTING_STEPS, createEmptyValues, valuesFromListing, getStepErrors, getFirstInvalidStep,
  LOCATION_STEP, hasPinnedCoordinates,
} from "./listing/listingFormModel";
import {
  PropertyStep, LocationFields, DetailsStep, PricingStep, PublishFields, ListingSummary,
} from "./listing/ListingFormFields";
import Select from "./Select";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./PropertiesPanel.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const PRESET_IMAGES = [
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80"
];

const ITEMS_PER_PAGE = 10;
const DEFAULT_LAT = 12.9716;
const DEFAULT_LNG = 77.5946;
const LAST_STEP = LISTING_STEPS.length - 1;
const CATEGORY_PILLS = [
  { id: "all", label: "All" },
  { id: "buy", label: "Buy" },
  { id: "rent", label: "Rent" },
  { id: "lease", label: "Lease" },
];
const STATUS_PILLS = [
  { id: "all", label: "All" },
  { id: "active", label: "Live" },
  { id: "inactive", label: "Hidden" },
  { id: "deleted", label: "Deleted" },
];
const UNASSIGNED_OWNER = { ownerId: null, ownerName: "", ownerPhone: "", ownerAvatar: "", ownerRole: "owner" };

// Status chip tone: live is positive, pending needs attention, deleted is
// destructive, hidden stays neutral.
const STATUS_CHIP = { active: "chip-success", pending: "chip-warning", deleted: "chip-danger" };

// "For Rent" -> "For rent": chips use sentence case across the portal.
const sentenceCase = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : "");

// Page numbers to show: first, last, and the current page's neighbours,
// with null marking a gap so long lists stay one row.
function pageList(total, current) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return sorted.reduce((out, p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(null);
    out.push(p);
    return out;
  }, []);
}

function CloseIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="5 12.5 10 17 19 7.5" />
    </svg>
  );
}

// 48px listing photo. A listing with no photo, or one that fails to load,
// gets a calm placeholder instead of an empty box.
function ListingThumb({ src }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="pp-thumb is-empty" aria-hidden="true">
        <Home size={20} variant="Linear" />
      </span>
    );
  }
  return (
    <span className="pp-thumb">
      <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
}

export default function PropertiesPanel() {
  const [properties, setProperties] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [globalFacilities, setGlobalFacilities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [attemptedSteps, setAttemptedSteps] = useState({});

  // The listing itself, in the shape the shared listing rules expect.
  const [values, setValues] = useState(createEmptyValues);
  const initialValuesRef = useRef(JSON.stringify(createEmptyValues()));
  const [assignedUser, setAssignedUser] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [locationMethod, setLocationMethod] = useState("map");
  const [mapLat, setMapLat] = useState(DEFAULT_LAT);
  const [mapLng, setMapLng] = useState(DEFAULT_LNG);
  // Whether the admin has actually placed the pin. The map opens on a
  // default point, and saving that untouched put listings in central Bengaluru.
  const [pinSet, setPinSet] = useState(false);
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const setField = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    // Querying the raw collection (no orderBy) so properties that are missing
    // or still resolving a `createdAt` server timestamp aren't silently
    // excluded from the snapshot: Firestore's orderBy drops any document
    // that lacks the ordered field. Sort client-side instead.
    const unsub1 = onSnapshot(
      collection(db, "properties"),
      (s) => {
        const l = [];
        s.forEach(d => l.push({ id: d.id, ...d.data() }));
        l.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setProperties(l);
        setLoaded(true);
      },
      (err) => { console.error("Failed to load properties:", err); setLoaded(true); }
    );
    const unsub2 = onSnapshot(collection(db, "users"), (s) => { const l = []; s.forEach(d => l.push({ id: d.id, ...d.data() })); setUsers(l); });
    const unsub3 = onSnapshot(collection(db, "facilities"), (s) => { const l = []; s.forEach(d => l.push({ id: d.id, ...d.data() })); setGlobalFacilities(l); });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  // --- Geocoding / Map helpers ---
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
      const data = await res.json();
      if (data?.address) {
        const a = data.address;
        const loc = a.suburb || a.neighbourhood || a.village || a.city_district || "";
        const cty = a.city || a.town || a.county || "";
        const street = a.road || a.subdivision || "";
        const houseNum = a.house_number || "";
        const addr = [houseNum, street].filter(Boolean).join(", ") || data.display_name.split(",")[0] || "";
        const postcode = String(a.postcode || "").replace(/\s/g, "");
        setValues((prev) => ({
          ...prev,
          locality: loc || prev.locality,
          city: cty || prev.city,
          address: addr || prev.address,
          state: a.state || prev.state,
          pincode: /^\d{6}$/.test(postcode) ? postcode : prev.pincode,
        }));
      }
    } catch (e) { console.warn("Reverse geocode failed:", e); }
  }, []);

  const parseGoogleMapsUrl = (url) => {
    try {
      let m = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/); if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
      m = url.match(/[?&](q|ll|query)=(-?\d+\.\d+),(-?\d+\.\d+)/); if (m) return { lat: parseFloat(m[2]), lng: parseFloat(m[3]) };
      m = url.match(/\/search\/(-?\d+\.\d+),\+?(-?\d+\.\d+)/); if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    } catch (e) {} return null;
  };

  const handleGoogleMapsUrlChange = async (url) => {
    setField("googleMapsUrl", url); if (!url.trim()) return;
    let target = url;
    if (url.includes("maps.app.goo.gl")) { try { const r = await fetch(`https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`); const t = await r.text(); const m = t.match(/https:\/\/www\.google\.com\/maps\/[^\s"<>\b]+/); if (m) target = m[0].replace(/&amp;/g, "&"); } catch(e){} }
    const coords = parseGoogleMapsUrl(target);
    if (coords) { setMapLat(coords.lat); setMapLng(coords.lng); setPinSet(true); if (mapRef.current && markerRef.current) { mapRef.current.setView([coords.lat, coords.lng], 15); markerRef.current.setLatLng([coords.lat, coords.lng]); } await reverseGeocode(coords.lat, coords.lng); }
  };

  const handleMapSearch = async (e) => {
    if (e) e.preventDefault(); if (!mapSearchQuery.trim()) return;
    try { const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=1`); const d = await r.json(); if (d?.length > 0) { const lat = parseFloat(d[0].lat), lon = parseFloat(d[0].lon); setMapLat(lat); setMapLng(lon); setPinSet(true); if (mapRef.current && markerRef.current) { mapRef.current.setView([lat, lon], 14); markerRef.current.setLatLng([lat, lon]); } await reverseGeocode(lat, lon); } else alert("Location not found."); } catch(e){ console.error("Map search failed:", e); }
  };

  useEffect(() => {
    if (!isModalOpen || wizardStep !== 1 || locationMethod !== "map") { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; } return; }
    const timer = setTimeout(() => {
      const c = document.getElementById("admin-map"); if (!c || mapRef.current) return;
      const map = L.map("admin-map").setView([mapLat, mapLng], 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: '&copy; OpenStreetMap' }).addTo(map);
      const marker = L.marker([mapLat, mapLng], { draggable: true }).addTo(map);
      markerRef.current = marker; mapRef.current = map;
      marker.on("dragend", async () => { const p = marker.getLatLng(); setMapLat(p.lat); setMapLng(p.lng); setPinSet(true); await reverseGeocode(p.lat, p.lng); });
      map.on("click", async (e) => { marker.setLatLng(e.latlng); setMapLat(e.latlng.lat); setMapLng(e.latlng.lng); setPinSet(true); await reverseGeocode(e.latlng.lat, e.latlng.lng); });
    }, 300);
    return () => { clearTimeout(timer); if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; } };
    // The map is created once per visit to the location step; later pin
    // moves update mapLat/mapLng without rebuilding it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, wizardStep, locationMethod]);

  // --- File / Save / Delete handlers ---
  const handleFileChange = (e) => { const f = Array.from(e.target.files).slice(0, 9); setSelectedFiles(f); setPreviewUrls(f.map(x => URL.createObjectURL(x))); };
  const removeFile = (i) => { setSelectedFiles(p => p.filter((_, j) => j !== i)); setPreviewUrls(p => p.filter((_, j) => j !== i)); };

  const uploadImages = async (pid) => {
    if (selectedFiles.length === 0) return [PRESET_IMAGES[selectedPresetIndex]];
    const urls = []; setUploadProgress(1);
    for (let i = 0; i < selectedFiles.length; i++) {
      const sRef = ref(storage, `properties/${pid}/image_${i}_${Date.now()}`);
      const task = uploadBytesResumable(sRef, selectedFiles[i]);
      await new Promise((res, rej) => { task.on("state_changed", (s) => setUploadProgress(Math.round(((i / selectedFiles.length) + (s.bytesTransferred / s.totalBytes / selectedFiles.length)) * 100)), rej, async () => { urls.push(await getDownloadURL(task.snapshot.ref)); res(); }); });
    }
    setUploadProgress(100); return urls;
  };

  const resolveStateName = async () => {
    if (values.state) return values.state;
    try { const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(`${values.address}, ${values.locality}, ${values.city}`)}&limit=1`); const d = await r.json(); if (d?.length > 0) { const pts = (d[0].display_name || "").split(","); if (pts.length > 2) return pts[pts.length - 3].trim(); } } catch(e){}
    // Same fallback the app uses when the state can't be resolved.
    return "Karnataka";
  };

  // Owner fields for the chosen user. A listing whose owner isn't in the
  // users list (e.g. a removed account) keeps its stored owner on edit.
  const resolveOwner = () => {
    if (!assignedUser) return UNASSIGNED_OWNER;
    const u = users.find(x => x.id === assignedUser);
    if (u) {
      return {
        ownerId: u.id,
        ownerName: u.name || u.phoneNumber || "Owner",
        ownerPhone: u.contactNumber || u.phoneNumber || "",
        ownerAvatar: u.avatarUrl || u.avatar || "",
        ownerRole: u.role || "owner",
      };
    }
    if (editingProperty && editingProperty.ownerId === assignedUser) {
      return {
        ownerId: editingProperty.ownerId,
        ownerName: editingProperty.ownerName || "",
        ownerPhone: editingProperty.ownerPhone || "",
        ownerAvatar: editingProperty.ownerAvatar || "",
        ownerRole: editingProperty.ownerRole || "owner",
      };
    }
    return UNASSIGNED_OWNER;
  };

  const resetForm = () => {
    const empty = createEmptyValues();
    setValues(empty);
    initialValuesRef.current = JSON.stringify(empty);
    setAssignedUser("");
    setSelectedFiles([]); setPreviewUrls([]); setSelectedPresetIndex(0);
    setMapLat(DEFAULT_LAT); setMapLng(DEFAULT_LNG); setPinSet(false); setMapSearchQuery(""); setLocationMethod("map");
    setWizardStep(0); setAttemptedSteps({}); setUploadProgress(0);
  };

  const closeModal = () => { setIsModalOpen(false); resetForm(); setEditingProperty(null); };

  // Clicking outside, the close button or Cancel shouldn't silently drop a half-filled listing.
  const requestClose = () => {
    if (isSaving) return;
    const dirty = JSON.stringify(values) !== initialValuesRef.current || selectedFiles.length > 0;
    if (dirty && !window.confirm("Discard this listing? Your changes will be lost.")) return;
    closeModal();
  };

  const openCreate = () => { setEditingProperty(null); resetForm(); setIsModalOpen(true); };

  const handleEditPress = (p) => {
    const next = valuesFromListing(p);
    setEditingProperty(p);
    setValues(next);
    initialValuesRef.current = JSON.stringify(next);
    setAssignedUser(p.ownerId || "");
    setSelectedFiles([]); setPreviewUrls([]); setSelectedPresetIndex(0);
    // A listing saved without a real pin opens with none, so it has to be
    // placed before saving, rather than keeping the default point.
    const pinned = hasPinnedCoordinates(p.latitude, p.longitude);
    setMapLat(pinned ? Number(p.latitude) : DEFAULT_LAT); setMapLng(pinned ? Number(p.longitude) : DEFAULT_LNG);
    setPinSet(pinned);
    setMapSearchQuery(""); setLocationMethod(p.googleMapsUrl ? "url" : "map");
    setWizardStep(0); setAttemptedSteps({});
    setIsModalOpen(true);
  };

  const goToStep = (step) => {
    // Arriving at the last step with no title: start from a suggestion.
    if (step === LAST_STEP && !String(values.title || "").trim()) setField("title", suggestTitle(values));
    setWizardStep(step);
  };

  const handleNext = () => {
    const errors = getStepErrors(wizardStep, values);
    const pinMissing = wizardStep === LOCATION_STEP && !pinSet;
    if (Object.keys(errors).length > 0 || pinMissing) {
      setAttemptedSteps((prev) => ({ ...prev, [wizardStep]: true }));
      return;
    }
    goToStep(wizardStep + 1);
  };

  const handleSave = async () => {
    const invalidStep = getFirstInvalidStep(values);
    if (invalidStep !== -1) {
      setAttemptedSteps((prev) => ({ ...prev, [invalidStep]: true }));
      setWizardStep(invalidStep);
      return;
    }
    if (!pinSet) {
      setAttemptedSteps((prev) => ({ ...prev, [LOCATION_STEP]: true }));
      setWizardStep(LOCATION_STEP);
      return;
    }
    setIsSaving(true);
    try {
      const isEdit = !!editingProperty;
      const dRef = isEdit ? doc(db, "properties", editingProperty.id) : doc(collection(db, "properties"));
      const images = isEdit && selectedFiles.length === 0 ? (editingProperty.images || []) : await uploadImages(dRef.id);
      const stateName = await resolveStateName();
      const data = buildListingDoc({
        id: dRef.id,
        values,
        latitude: mapLat,
        longitude: mapLng,
        stateName,
        images,
        owner: resolveOwner(),
        isEdit,
      });
      await setDoc(dRef, data, { merge: true });
      closeModal();
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Couldn't save this listing: ${err?.message || err}`);
    } finally { setIsSaving(false); setUploadProgress(0); }
  };

  // A live listing is tombstoned like a delete in the app (links, saved lists
  // and leads keep resolving); only an already-deleted one can be erased.
  const handleDelete = async (p) => {
    const status = getListingStatus(p);
    if (status === "deleted") {
      if (!window.confirm(`Permanently delete "${p.title || "this listing"}"? This can't be undone. Saved lists, leads and chat cards will no longer find it.`)) return;
      try { await deleteListingPermanently(p.id); } catch (err) { console.error("Permanent delete failed:", err); alert(`Couldn't delete this listing: ${err?.message || err}`); }
      return;
    }
    if (!window.confirm(`Delete "${p.title || "this listing"}"? It will be removed from search and its Reels hidden.`)) return;
    try { await softDeleteListing(p.id, "Removed by admin"); } catch (err) { console.error("Delete failed:", err); alert(`Couldn't delete this listing: ${err?.message || err}`); }
  };

  const handleToggleHidden = async (p) => {
    const hide = getListingStatus(p) !== "inactive";
    try { await setListingHidden(p.id, hide); } catch (err) { console.error("Status change failed:", err); alert(`Couldn't ${hide ? "hide" : "show"} this listing: ${err?.message || err}`); }
  };

  const nonDeleted = properties.filter(p => getListingStatus(p) !== "deleted");

  const filtered = properties.filter(p => {
    const q = searchQuery.trim().toLowerCase();
    const s = !q || [p.title, p.locality, p.city, p.societyName, p.ownerName].some(x => String(x || "").toLowerCase().includes(q));
    const c = activeCategory === "all" || getListingTypeOf(p) === activeCategory;
    const st = statusFilter === "all" || getListingStatus(p) === statusFilter;
    return s && c && st;
  });

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, activeCategory, statusFilter]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProperties = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const stepErrors = attemptedSteps[wizardStep] ? getStepErrors(wizardStep, values) : {};
  const previewImage = previewUrls[0] || (editingProperty && selectedFiles.length === 0 && editingProperty.images?.[0]) || PRESET_IMAGES[selectedPresetIndex];

  const hasFilters = !!searchQuery.trim() || activeCategory !== "all" || statusFilter !== "all";
  const clearFilters = () => { setSearchQuery(""); setActiveCategory("all"); setStatusFilter("all"); };
  const statValue = (n) => (loaded ? n : <span className="skeleton pp-sk-stat" aria-hidden="true" />);

  return (
    <div className="pp">
      <div className="stat-strip">
        <div className="stat">
          <span className="stat-label">Total properties</span>
          <span className="stat-value">{statValue(nonDeleted.length)}</span>
          <span className="stat-note">Excludes deleted listings</span>
        </div>
        <div className="stat">
          <span className="stat-label">Residential</span>
          <span className="stat-value">{statValue(nonDeleted.filter(p => p.propertyCategory === "residential").length)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Commercial</span>
          <span className="stat-value">{statValue(nonDeleted.filter(p => p.propertyCategory === "commercial").length)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">For lease</span>
          <span className="stat-value">{statValue(nonDeleted.filter(p => getListingTypeOf(p) === "lease").length)}</span>
        </div>
      </div>

      <div className="toolbar pp-toolbar">
        <div className="search-field pp-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            type="text"
            className="input"
            placeholder="Search title, locality, project, owner"
            aria-label="Search properties"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="segmented" role="group" aria-label="Transaction">
          {CATEGORY_PILLS.map(pill => (
            <button key={pill.id} type="button" aria-pressed={activeCategory === pill.id} onClick={() => setActiveCategory(pill.id)}>
              {pill.label}
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Listing status">
          {STATUS_PILLS.map(pill => (
            <button key={pill.id} type="button" aria-pressed={statusFilter === pill.id} onClick={() => setStatusFilter(pill.id)}>
              {pill.label}
            </button>
          ))}
        </div>
        <div className="toolbar-spacer" />
        <button type="button" className="btn-primary pp-add" onClick={openCreate}>
          <Add size={18} variant="Linear" aria-hidden="true" />
          Add property
        </button>
      </div>

      {loaded && paginatedProperties.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon"><Home size={22} variant="Linear" aria-hidden="true" /></span>
            {properties.length === 0 ? (
              <>
                <h3>No properties yet</h3>
                <p>Listings published by dealers and owners in the app appear here. You can also add one with Add property.</p>
              </>
            ) : (
              <>
                <h3>No properties match</h3>
                <p>Nothing matches this search and these filters. Try another title, locality or owner, or show every listing.</p>
                {hasFilters && <button type="button" className="btn-secondary btn-sm" onClick={clearFilters}>Clear filters</button>}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="table-wrap pp-table-wrap">
          <table className="table pp-table">
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">Transaction</th>
                <th scope="col" className="num">Price</th>
                <th scope="col">Type</th>
                <th scope="col" className="pp-col-owner">Owner</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody aria-busy={!loaded}>
              {!loaded && Array.from({ length: 5 }, (_, i) => (
                <tr key={`sk-${i}`} className="pp-row pp-row-skeleton" aria-hidden="true">
                  <td className="pp-c-listing">
                    <div className="pp-listing">
                      <span className="skeleton pp-sk-thumb" />
                      <span className="pp-listing-text">
                        <span className="skeleton pp-sk-line" />
                        <span className="skeleton pp-sk-line is-short" />
                      </span>
                    </div>
                  </td>
                  <td className="pp-c-tx"><span className="skeleton pp-sk-chip" /></td>
                  <td className="num pp-c-price"><span className="skeleton pp-sk-text" /></td>
                  <td className="pp-c-type"><span className="skeleton pp-sk-text" /></td>
                  <td className="pp-col-owner pp-c-owner"><span className="skeleton pp-sk-text" /></td>
                  <td className="pp-c-status"><span className="skeleton pp-sk-chip" /></td>
                  <td className="pp-c-actions" />
                </tr>
              ))}
              {loaded && paginatedProperties.map(p => {
                const info = getListingCardInfo(p);
                const isDeleted = info.status === "deleted";
                const isHidden = info.status === "inactive";
                const name = p.title || "this listing";
                const place = [p.locality, p.city].filter(Boolean).join(", ") || p.address;
                const hasPrice = toNumber(p.price) > 0;
                const typeMeta = [
                  info.configLabel && info.configLabel !== info.typeLabel ? info.configLabel : "",
                  info.bathroomsLabel,
                  info.areaLabel,
                ].filter(Boolean).join(" · ");
                return (
                  <tr key={p.id} className={`pp-row ${isDeleted || isHidden ? "is-muted" : ""}`}>
                    <td className="pp-c-listing">
                      <div className="pp-listing">
                        <ListingThumb key={p.images?.[0] || "none"} src={p.images?.[0]} />
                        <span className="pp-listing-text">
                          <span className="pp-listing-title" title={p.title}>{p.title || "Untitled listing"}</span>
                          {place && <span className="pp-listing-loc" title={place}>{place}</span>}
                        </span>
                      </div>
                    </td>
                    <td className="pp-c-tx"><span className="chip">{sentenceCase(info.badge)}</span></td>
                    <td className="num pp-c-price">
                      {hasPrice ? (
                        <>
                          <span className="pp-price">{info.priceLabel}</span>
                          {info.priceUnit && <span className="pp-price-unit">{info.priceUnit}</span>}
                        </>
                      ) : (
                        <span className="pp-price-none">{info.priceLabel}</span>
                      )}
                    </td>
                    <td className="pp-c-type">
                      <span className="pp-type">{info.typeLabel}</span>
                      {typeMeta && <span className="pp-type-meta">{typeMeta}</span>}
                    </td>
                    <td className="pp-col-owner pp-c-owner" data-label="Owner">
                      {p.ownerName ? <span className="pp-owner">{p.ownerName}</span> : <span className="muted">Unassigned</span>}
                    </td>
                    <td className="pp-c-status">
                      <span className={`chip chip-dot ${STATUS_CHIP[info.status] || ""}`}>
                        {info.status === "active" ? "Live" : info.statusLabel}
                      </span>
                    </td>
                    <td className="pp-c-actions">
                      <div className="pp-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleEditPress(p)}
                          title="Edit"
                          aria-label={`Edit ${name}`}
                        >
                          <Edit2 size={18} variant="Linear" />
                        </button>
                        {!isDeleted && (
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleToggleHidden(p)}
                            title={isHidden ? "Show in search" : "Hide from search"}
                            aria-label={isHidden ? `Show ${name} in search` : `Hide ${name} from search`}
                          >
                            {isHidden ? <Eye size={18} variant="Linear" /> : <EyeSlash size={18} variant="Linear" />}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-icon pp-action-danger"
                          onClick={() => handleDelete(p)}
                          title={isDeleted ? "Delete permanently" : "Delete"}
                          aria-label={isDeleted ? `Delete ${name} permanently` : `Delete ${name}`}
                        >
                          <Trash size={18} variant="Linear" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loaded && <span className="sr-only" role="status">Loading properties</span>}
        </div>
      )}

      {loaded && filtered.length > 0 && (
        <nav className="pagination" aria-label="Properties pages">
          <span>
            Showing <strong className="pp-page-range">{startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filtered.length)}</strong> of {filtered.length} {filtered.length === 1 ? "property" : "properties"}
          </span>
          {totalPages > 1 && (
            <div className="pagination-pages">
              <button type="button" className="page-btn" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                <ArrowLeft2 size={16} variant="Linear" />
              </button>
              {pageList(totalPages, currentPage).map((pg, i) => (
                pg === null ? (
                  <span key={`gap-${i}`} className="pp-page-gap" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={pg}
                    type="button"
                    className={`page-btn ${pg === currentPage ? "active" : ""}`}
                    aria-current={pg === currentPage ? "page" : undefined}
                    aria-label={`Page ${pg}`}
                    onClick={() => setCurrentPage(pg)}
                  >
                    {pg}
                  </button>
                )
              ))}
              <button type="button" className="page-btn" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                <ArrowRight2 size={16} variant="Linear" />
              </button>
            </div>
          )}
        </nav>
      )}

      {/* Add / Edit Wizard */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={requestClose}>
          <div
            className="modal pp-wizard lf-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pp-wizard-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="pp-wizard-title">{editingProperty ? "Edit property" : "New property"}</h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={requestClose}>
                <CloseIcon />
              </button>
            </div>

            <ol className="pp-steps" aria-label="Steps">
              {LISTING_STEPS.map((s, i) => (
                <li
                  key={s.id}
                  className={`pp-step ${i === wizardStep ? "is-active" : ""} ${i < wizardStep ? "is-done" : ""}`}
                  aria-current={i === wizardStep ? "step" : undefined}
                >
                  <span className="pp-step-num">
                    {i < wizardStep ? <CheckIcon /> : i + 1}
                  </span>
                  <span className="pp-step-label">{s.label}</span>
                  {i < wizardStep && <span className="sr-only">, done</span>}
                </li>
              ))}
            </ol>

            <div className="modal-body pp-wizard-body">
              {wizardStep === 0 && (
                <PropertyStep values={values} setValues={setValues} errors={stepErrors} />
              )}

              {wizardStep === 1 && (
                <>
                  <section className="pp-section">
                    <h3 className="pp-section-title">Pin location</h3>
                    <div className="segmented pp-method" role="group" aria-label="How to set the location">
                      <button type="button" aria-pressed={locationMethod === "map"} onClick={() => setLocationMethod("map")}>
                        <Map1 size={16} variant="Linear" aria-hidden="true" />
                        Search on map
                      </button>
                      <button type="button" aria-pressed={locationMethod === "url"} onClick={() => setLocationMethod("url")}>
                        <Link2 size={16} variant="Linear" aria-hidden="true" />
                        Paste Maps link
                      </button>
                    </div>
                    {locationMethod === "url" ? (
                      <div className="field">
                        <label className="field-label" htmlFor="pp-maps-url">Google Maps link</label>
                        <input
                          id="pp-maps-url"
                          type="text"
                          className="input"
                          placeholder="https://maps.app.goo.gl/... or a full Maps URL"
                          value={values.googleMapsUrl}
                          onChange={e => handleGoogleMapsUrlChange(e.target.value)}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="pp-map-search">
                          <input
                            type="text"
                            className="input"
                            placeholder="Search for a place"
                            aria-label="Search for a place on the map"
                            value={mapSearchQuery}
                            onChange={e => setMapSearchQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleMapSearch(e); }}
                          />
                          <button type="button" className="btn-secondary" onClick={handleMapSearch}>Search</button>
                        </div>
                        <div id="admin-map" className="pp-map" />
                        <p className="pp-map-coords">
                          <Location size={14} variant="Linear" aria-hidden="true" />
                          {pinSet ? `${mapLat.toFixed(5)}, ${mapLng.toFixed(5)}` : "No pin yet"}
                        </p>
                      </>
                    )}
                    {attemptedSteps[LOCATION_STEP] && !pinSet && (
                      <div className="field-error" role="alert">
                        Pin where the property is: click the map or drag the pin, search for the place, or paste a Google Maps link that includes coordinates.
                      </div>
                    )}
                  </section>

                  <section className="pp-section">
                    <h3 className="pp-section-title">Address</h3>
                    <LocationFields values={values} setField={setField} errors={stepErrors} />
                  </section>
                </>
              )}

              {wizardStep === 2 && (
                <DetailsStep values={values} setField={setField} errors={stepErrors} />
              )}

              {wizardStep === 3 && (
                <PricingStep values={values} setField={setField} setValues={setValues} errors={stepErrors} />
              )}

              {wizardStep === LAST_STEP && (
                <>
                  <section className="pp-section">
                    <ListingSummary values={values} imageUrl={previewImage} />
                  </section>

                  <section className="pp-section">
                    <PublishFields values={values} setField={setField} errors={stepErrors} facilities={globalFacilities} />
                  </section>

                  <section className="pp-section">
                    <div className="field">
                      <label className="field-label" htmlFor="pp-assign">Assign to</label>
                      <Select
                        id="pp-assign"
                        value={assignedUser}
                        onChange={setAssignedUser}
                        searchPlaceholder="Search name or phone"
                        options={[
                          { value: "", label: "Unassigned", detail: "No contact shown to buyers" },
                          ...users.map(u => ({ value: u.id, label: u.name || "Unnamed", detail: u.phoneNumber || u.email || u.id })),
                        ]}
                      />
                    </div>
                  </section>

                  <section className="pp-section">
                    <h3 className="pp-section-title">Property images</h3>
                    <div className="pp-upload">
                      <input type="file" multiple accept="image/*" onChange={handleFileChange} id="property-images" className="pp-upload-input" />
                      <label htmlFor="property-images" className="pp-upload-label">
                        <GalleryAdd size={24} variant="Linear" aria-hidden="true" />
                        <span className="pp-upload-title">Choose images to upload</span>
                        <span className="pp-upload-hint">{selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : editingProperty ? `Keeps the current ${editingProperty.images?.length || 0} photo(s) unless you upload new ones` : "PNG or JPG, up to 9 images"}</span>
                      </label>
                    </div>
                    {previewUrls.length > 0 && (
                      <div className="pp-previews">
                        {previewUrls.map((u, i) => (
                          <div key={i} className="pp-preview">
                            <img src={u} alt="" />
                            <button type="button" className="pp-preview-remove" aria-label={`Remove image ${i + 1}`} onClick={() => removeFile(i)}>
                              <CloseIcon size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedFiles.length === 0 && !editingProperty && (
                      <>
                        <p className="pp-or"><span>or choose a preset</span></p>
                        <div className="pp-presets" role="group" aria-label="Preset images">
                          {PRESET_IMAGES.map((img, i) => (
                            <button
                              key={i}
                              type="button"
                              className={`pp-preset ${selectedPresetIndex === i ? "is-selected" : ""}`}
                              aria-pressed={selectedPresetIndex === i}
                              aria-label={`Preset image ${i + 1}`}
                              onClick={() => setSelectedPresetIndex(i)}
                            >
                              <img src={img} alt="" />
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {uploadProgress > 0 && (
                      <div className="pp-progress" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Upload progress">
                        <div className="pp-progress-track">
                          <div className="pp-progress-fill" style={{ width: `${uploadProgress}%` }} />
                        </div>
                        <span className="pp-progress-text">Uploading {uploadProgress}%</span>
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>

            <div className="modal-footer pp-wizard-footer">
              {wizardStep > 0 && (
                <button type="button" className="btn-ghost" onClick={() => setWizardStep(wizardStep - 1)} disabled={isSaving}>
                  <ArrowLeft2 size={16} variant="Linear" aria-hidden="true" />
                  Back
                </button>
              )}
              <div className="pp-wizard-footer-end">
                <button type="button" className="btn-secondary" onClick={requestClose} disabled={isSaving}>Cancel</button>
                {wizardStep < LAST_STEP ? (
                  <button type="button" className="btn-primary" onClick={handleNext}>
                    Next
                    <ArrowRight2 size={16} variant="Linear" aria-hidden="true" />
                  </button>
                ) : (
                  <button type="button" className="btn-primary" disabled={isSaving} onClick={handleSave}>
                    {isSaving && <span className="btn-spinner" aria-hidden="true" />}
                    {isSaving ? "Saving" : editingProperty ? "Save changes" : "Publish"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
